'use client'

import React, { useCallback, useMemo } from 'react'
import { FolderWithFiles } from 'solar-icons'
import { useSearchParams } from '@/lib/hooks'
import { cn } from '@/lib/utils'

import {
  Button,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui'
import { CategoryButton } from '@/components/molecules'

interface ProductsFilterBarProps extends React.ComponentPropsWithoutRef<'div'> {
  filters: any[]
}

export const ProductsFilterBar = React.memo(
  ({ className, filters }: ProductsFilterBarProps) => {
    const { productFilterId, setSearchParams } = useSearchParams()

    const handleClick = useCallback(() => {
      setSearchParams({ productFilterId: null })
    }, [setSearchParams])

    // Memoize filter items to prevent unnecessary re-renders
    const filterItems = useMemo(() => {
      return filters?.map(({ title, id }, index) => (
        <CarouselItem key={id}>
          <CategoryButton
            filterId={id}
            title={title}
            isActive={productFilterId === id}
          />
        </CarouselItem>
      ))
    }, [filters, productFilterId])

    return (
      <div
        className={cn(
          'flex h-[var(--product-filter-bar-height)] shrink-0 items-center gap-2',
          className,
        )}
      >
        <Button
          variant={!productFilterId ? 'default' : 'outline'}
          className="size-11 shrink-0 justify-center p-0"
          onClick={handleClick}
        >
          <FolderWithFiles className="size-4 shrink-0" strokeWidth={1.5} />
        </Button>

        <div className="flex w-full overflow-hidden">
          <Carousel
            className="w-full overflow-hidden rounded-lg"
            opts={{ slidesToScroll: 3, dragFree: true }}
          >
            <CarouselContent className="gap-2">{filterItems}</CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </div>
      </div>
    )
  },
)

ProductsFilterBar.displayName = 'ProductsFilterBar'
